import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { EXIT_REASONS, OTHER_EXIT_REASON, exitReasonLabel } from "../../lib/exitReasons";
import { todayStr } from "../../lib/date";

const todayIso = () => todayStr();

// Prompts the admin to confirm the end date, Exit Reason and Departure Notes for
// the CURRENT enrollment period when a student is marked Former/Graduated.
export function EndEnrollmentDialog({ open, onOpenChange, studentName, defaultEndDate, defaultReason, onConfirm }) {
  const [endDate, setEndDate] = React.useState(defaultEndDate || todayIso());
  const [exitReason, setExitReason] = React.useState("");
  const [exitReasonOther, setExitReasonOther] = React.useState("");
  const [departureNotes, setDepartureNotes] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setEndDate(defaultEndDate || todayIso());
      setExitReason(EXIT_REASONS.includes(defaultReason) ? defaultReason : "");
      setExitReasonOther("");
      setDepartureNotes("");
    }
  }, [open, defaultEndDate, defaultReason]);

  const isOther = exitReason === OTHER_EXIT_REASON;
  const canSave = !!endDate && !!exitReason && (!isOther || exitReasonOther.trim().length > 0);
  // Keep the enrollment-period reason populated with the resolved exit reason.
  const leavingReason = exitReasonLabel(exitReason, exitReasonOther);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="end-enrollment-dialog">
        <DialogHeader>
          <DialogTitle>End Enrollment Period</DialogTitle>
          <DialogDescription>
            Confirm the end date, exit reason, and any departure notes for {studentName || "this student"}'s current enrollment period. This is saved to the student record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>End Date</Label>
            <Input
              type="date"
              value={endDate}
              data-testid="end-enrollment-date"
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Exit Reason</Label>
            <Select value={exitReason} onValueChange={setExitReason}>
              <SelectTrigger data-testid="end-enrollment-exit-reason-select"><SelectValue placeholder="Select an exit reason…" /></SelectTrigger>
              <SelectContent>
                {EXIT_REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`end-enrollment-exit-reason-${r}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isOther && (
              <Input
                placeholder="Please specify the reason"
                value={exitReasonOther}
                data-testid="end-enrollment-exit-reason-other"
                onChange={(e) => setExitReasonOther(e.target.value)}
              />
            )}
            <p className="text-xs text-muted-foreground">
              Used on the Referral Trends dashboard to split former students into Graduates and Disenrolled.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Departure Notes</Label>
            <Textarea
              placeholder="Any additional context about this student's departure (optional)"
              value={departureNotes}
              rows={4}
              data-testid="end-enrollment-departure-notes"
              onChange={(e) => setDepartureNotes(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="end-enrollment-cancel">Cancel</Button>
          <Button
            data-testid="end-enrollment-confirm"
            disabled={!canSave}
            onClick={() => onConfirm({
              end_date: endDate,
              exit_reason: exitReason,
              exit_reason_other: isOther ? exitReasonOther.trim() : "",
              departure_notes: departureNotes,
              leaving_reason: leavingReason,
            })}
          >
            Confirm &amp; Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
